/**
 * The barcode prefixes as set up in BOB (task 2201, owner 2026-10-14), instead of only the ones
 * built into this site.
 *
 * `GET /api/barcoding-settings/barcodes` answers each kind of code's prefixes for the site and
 * each partner's own pallet and box prefixes. The tablet reads it at log-in and keeps the last
 * answer, so a tablet out of range still reads labels the same way; a tablet that has never been
 * told, or an answer it cannot use, keeps the built-in prefixes (`scanTypes.ts`), which are also
 * BOB's defaults. A site setting, not anyone's work: it is not cleared at a session boundary.
 */
import { SCAN_TYPES, type ScanType } from './scanTypes'

const KEY = 'dockautomation.barcodePrefixes'
const MAX_LENGTH = 10

/** The kinds BOB sets prefixes for. A container is known by its shape and has none. */
export const SET_IN_BOB: readonly ScanType[] = ['pallet', 'box', 'door', 'gang', 'trip', 'location', 'username', 'password', 'employee']

/** The built-in prefixes, as this site shipped them. */
const BUILT_IN: Record<ScanType, string[]> = Object.fromEntries(
  (Object.keys(SCAN_TYPES) as ScanType[]).map(type => [type, [...SCAN_TYPES[type].prefixes]]),
) as Record<ScanType, string[]>

export type PrefixMap = Partial<Record<ScanType, string[]>>

function isPrefixList(value: unknown): value is string[] {
  return Array.isArray(value) && value.length > 0
    && value.every(p => typeof p === 'string' && p.length <= MAX_LENGTH)
}

/**
 * What the server said, made safe: each kind with a usable list, and every partner's own pallet
 * and box prefixes added to the site's (a partner's label is a pallet whichever partner it is).
 * Anything unusable is left out, so that kind keeps its built-in prefixes.
 */
export function parseBarcodeSettings(value: unknown): PrefixMap {
  const raw = value && typeof value === 'object' ? value as Record<string, unknown> : {}
  const map: PrefixMap = {}
  for (const entry of Array.isArray(raw.types) ? raw.types : []) {
    const item = entry && typeof entry === 'object' ? entry as Record<string, unknown> : {}
    const type = item.type as ScanType
    if (SET_IN_BOB.includes(type) && isPrefixList(item.prefixes)) map[type] = [...item.prefixes]
  }
  for (const entry of Array.isArray(raw.partners) ? raw.partners : []) {
    const item = entry && typeof entry === 'object' ? entry as Record<string, unknown> : {}
    for (const [type, field] of [['pallet', 'palletPrefixes'], ['box', 'boxPrefixes']] as const) {
      const own = item[field]
      if (!Array.isArray(own)) continue
      const usable = own.filter((p): p is string => typeof p === 'string' && p.trim() !== '' && p.length <= MAX_LENGTH)
      if (usable.length === 0) continue
      const list = map[type] ?? [...BUILT_IN[type]]
      map[type] = [...list, ...usable.filter(p => !list.some(q => q.toUpperCase() === p.toUpperCase()))]
    }
  }
  return map
}

/** Read labels with these prefixes from now on; a kind not in the map goes back to its built-in set. */
export function applyBarcodePrefixes(map: PrefixMap): void {
  for (const type of SET_IN_BOB) SCAN_TYPES[type].prefixes = [...(map[type] ?? BUILT_IN[type])]
}

type Store = Pick<Storage, 'getItem' | 'setItem'>

function storage(): Store | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

export function loadBarcodePrefixes(store: Store | null = storage()): PrefixMap {
  try {
    const kept = JSON.parse(store?.getItem(KEY) ?? '{}') as Record<string, unknown>
    const map: PrefixMap = {}
    for (const type of SET_IN_BOB) if (isPrefixList(kept[type])) map[type] = [...(kept[type] as string[])]
    return map
  } catch {
    return {}
  }
}

export function saveBarcodePrefixes(map: PrefixMap, store: Store | null = storage()): void {
  try {
    store?.setItem(KEY, JSON.stringify(map))
  } catch {
    // Storage unavailable: the tablet reads them again at the next log-in.
  }
}
